import { produce } from 'immer';
import { DateTime } from 'luxon';
import { get, isNil, set } from 'lodash/fp';
import { QueryState } from 'app/lib/constants';
import { extractError } from 'app/lib/error';
import { toStringFqn } from 'app/lib/utils';
import { REHYDRATE } from 'app/store/types.js';
import { INVALIDATE_DATASET } from 'app/store/datasetData/types.js';
import {
    CANCEL_QUERY,
    CANCEL_ALL_QUERIES,
    DELETE_DATASET_QUERY,
    DELETE_JOIN_QUERIES,
    DELETE_EXPLORE_QUERY,
    DELETE_FQN_QUERY,
    DELETE_EXTRACT_JSON_QUERIES,
    EXECUTE_SQL_QUERY,
    EXECUTE_DATASET_QUERY,
    EXECUTE_TRANSFORMATION_QUERY,
    EXECUTE_EXPLORE_QUERY,
    EXECUTE_FQN_QUERY,
    EXECUTE_ROW_COUNT_QUERY,
    GET_QUERY_RESULTSET,
    GET_QUERY_STATUS,
    SUBMIT_QUERY,
    CLEANUP_AND_INVALIDATE_QUERIES,
    INVALIDATE_EXPLORE_QUERIES,
    DELETE_ROW_COUNT_QUERY,
    RESTORE_QUERY,
    DELETE_FILTERED_METRICS_QUERIES,
    DELETE_QUERY,
    EXECUTE_COLUMN_MANAGEMENT_PREVIEW_QUERY,
    DELETE_MULTIJOIN_VALIDATION_QUERIES
} from './types.js';
import { deletePath, ensurePathExists, parseKey } from './utils.js';

export const QueryKeys = {
    COLUMN_MAPPING_PREVIEW: 'columnMapping.preview',
    ENTITIES: 'byEntityId',
    FQNS: 'byFQN',
    JOIN_PREVIEW: 'operations.join.preview',
    JOIN_KEYS: 'operations.join.validations.joinKeys',
    JOIN_LEFT_UNMATCHED_ROWS: 'operations.join.validations.leftUnmatchedRows',
    JOIN_RIGHT_UNMATCHED_ROWS: 'operations.join.validations.rightUnmatchedRows',
    SQL_PREVIEW: 'operations.sql.preview',
    FORMULA_PREVIEW: 'operations.formula.preview',
    SUMMARIZE_PREVIEW: 'operations.summarize.preview',
    UNION_PREVIEW: 'operations.union.preview',
    ROW_COUNT: 'metrics.rowCount.byFqnOrId',
    EXTRACT_JSON_PREVIEW: 'operations.extractJson.preview',
    EXTRACT_JSON_METRICS: 'operations.extractJson.metrics',
    NOTEBOOK_PREVIEW: 'operations.notebook.preview',
    EXPLORE_PREVIEW: 'explore.preview',
    USAGE_REPORTING_WAREHOUSE_USAGE: 'usageReporting.warehouseUsage',
    USAGE_REPORTING_WAREHOUSES: 'usageReporting.warehouses',
    ROI_REPORTING_ALL_TABLE_METRICS: 'ROIReporting.tableMetrics.all',
    ROI_REPORTING_FILTERED_TABLE_METRICS: 'ROIReporting.tableMetrics.filtered',
    ROI_REPORTING_ASSET_ROI: 'ROIReporting.tableCube',
    ROI_TABLE_CUBE_WAREHOUSE_VALUES: 'ROIReporting.tableCubeValues.warehouses',
    ROI_TABLE_CUBE_USER_VALUES: 'ROIReporting.tableCubeValues.users',
    ROI_TABLE_CUBE_TABLE_VALUES: 'ROIReporting.tableCubeValues.tables',
    USAGE_REPORTING_WAREHOUSE_CUBE: 'usageReporting.warehouseCube',
    USAGE_REPORTING_WAREHOUSE_CUBE_WAREHOUSE_VALUES:
        'usageReporting.warehouseCube.values.warehouses',
    USAGE_REPORTING_WAREHOUSE_CUBE_USER_VALUES: 'usageReporting.warehouseCube.values.users',
    FILTER_ROW_COUNTS: 'operations.filter.rowCounts',
    FILTER_PREVIEW: 'operations.filter.preview',
    MULTI_JOIN_PREVIEW: 'operations.multiJoin.preview',
    MULTI_JOIN_UNMATCHEDROWS: 'operations.multiJoin.validations.unmatchedRows',
    MULTI_JOIN_MATCHEDROWS: 'operations.multiJoin.validations.matchedRows',
    MULTI_JOIN_MULTIPLICATION: 'operations.multiJoin.validations.multiplication',
    DATASOURCE_FRESHNESS_CHECK: 'workbench.datasourceFreshnessCheck'
};

export const INITIAL_STATE = {
    isCancelingAllQueries: false,
    byQueryId: {},
    byEntityId: {},
    byExploreQueryUuid: {},
    byFQN: {},
    columnMapping: {
        preview: {}
    },
    operations: {
        join: {
            preview: {},
            validations: {
                leftUnmatchedRows: {},
                rightUnmatchedRows: {},
                joinKeys: {}
            }
        },
        sql: {
            preview: {}
        },
        formula: {
            preview: {}
        },
        summarize: {
            preview: {}
        },
        union: {
            preview: {}
        },
        extractJson: {
            preview: {},
            metrics: {}
        },
        notebook: {
            preview: {}
        },
        filter: {
            rowCounts: {}
        },
        multiJoin: {
            preview: {},
            validations: {
                matchedRows: {},
                unmatchedRows: {},
                multiplication: {}
            }
        }
    },
    metrics: {
        rowCount: {
            byFqnOrId: {}
        },
        dimensionFilters: {
            byQueryUuid: {}
        }
    },
    ROIReporting: {
        tableMetrics: {
            all: {},
            filtered: {}
        },
        tableCube: {},
        tableCubeValues: {
            warehouses: {},
            users: {},
            tables: {}
        }
    },
    usageReporting: {
        warehouseUsage: {},
        warehouseCube: {},
        warehouseCubeValues: {
            warehouses: {},
            users: {}
        }
    },
    workbench: {
        datasourceFreshnessCheck: {}
    }
};

export const snowflakeQueriesReducer = produce((draft, action) => {
    switch (action.type) {
        case INVALIDATE_EXPLORE_QUERIES: {
            const { explorationQueries } = action.payload;

            explorationQueries.forEach((query) => {
                const { queryUuid } = query.model;
                const snowflakeQuery = draft.byExploreQueryUuid[queryUuid];

                if (snowflakeQuery?.queryId in draft.byQueryId) {
                    draft.byQueryId[snowflakeQuery.queryId].state = QueryState.INVALID;
                }
            });

            break;
        }

        case INVALIDATE_DATASET: {
            const { entityId } = action.payload;
            const { byFqnOrId } = draft.metrics.rowCount;

            if (entityId in byFqnOrId) {
                delete draft.metrics.rowCount.byFqnOrId[entityId];
            }
            break;
        }

        case EXECUTE_FQN_QUERY.STARTED:
        case EXECUTE_DATASET_QUERY.STARTED:
        case EXECUTE_EXPLORE_QUERY.STARTED:
        case EXECUTE_ROW_COUNT_QUERY.STARTED:
        case EXECUTE_SQL_QUERY.STARTED:
        case EXECUTE_TRANSFORMATION_QUERY.STARTED:
        case EXECUTE_COLUMN_MANAGEMENT_PREVIEW_QUERY.STARTED: {
            const { queryKey, fqn, fullData, startedAt } = action.meta;

            ensurePathExists(draft, parseKey(queryKey));

            const metadata = get(queryKey, draft);

            if (metadata.queryId) {
                delete draft.byQueryId[metadata.queryId];
            }

            Object.assign(metadata, {
                state: QueryState.RUNNING,
                queryId: null,
                error: null,
                fullData,
                fqn,
                startedAt,
                sampledCachingTables: null,
                sampledInputTables: null,
                meta: action.meta
            });

            break;
        }

        case EXECUTE_FQN_QUERY.SUCCEEDED:
        case EXECUTE_DATASET_QUERY.SUCCEEDED:
        case EXECUTE_ROW_COUNT_QUERY.SUCCEEDED:
        case EXECUTE_SQL_QUERY.SUCCEEDED:
        case EXECUTE_TRANSFORMATION_QUERY.SUCCEEDED:
        case EXECUTE_EXPLORE_QUERY.SUCCEEDED:
        case EXECUTE_COLUMN_MANAGEMENT_PREVIEW_QUERY.SUCCEEDED: {
            const { queryKey } = action.meta;
            const metadata = get(queryKey, draft);
            const { sql, sampledCachingTables, sampledInputTables } = action.payload.data;

            if (metadata) {
                metadata.sql = sql;
                metadata.sampledCachingTables = sampledCachingTables;
                metadata.sampledInputTables = sampledInputTables?.map(({ table }) => table);
            }

            break;
        }

        case EXECUTE_FQN_QUERY.FAILED:
        case EXECUTE_DATASET_QUERY.FAILED:
        case EXECUTE_ROW_COUNT_QUERY.FAILED:
        case EXECUTE_SQL_QUERY.FAILED:
        case EXECUTE_TRANSFORMATION_QUERY.FAILED:
        case EXECUTE_EXPLORE_QUERY.FAILED:
        case EXECUTE_COLUMN_MANAGEMENT_PREVIEW_QUERY.FAILED: {
            const { queryKey } = action.meta;
            const metadata = get(queryKey, draft);

            if (metadata) {
                metadata.error = extractError(action.payload);
                metadata.state = QueryState.FAILED;
            }

            break;
        }

        case SUBMIT_QUERY.SUCCEEDED: {
            const { queryKey } = action.meta;
            const { queryId } = action.payload;

            const metadata = get(queryKey, draft);

            if (metadata && metadata.state !== QueryState.CANCELED) {
                metadata.queryId = queryId;
                draft.byQueryId[queryId] = {
                    state: QueryState.RUNNING,
                    queryKey,
                    queryId,
                    isQueued: true
                };
            }

            break;
        }

        case GET_QUERY_STATUS.SUCCEEDED: {
            const { id } = action.params;
            const { isStillRunning, isStale, isQueued } = action.payload;
            const query = draft.byQueryId[id];

            if (isNil(query)) {
                console.warn('query status for null query', action);
            }

            if (!isNil(query)) {
                query.isQueued = isQueued;

                if (isStillRunning && query.state !== QueryState.CANCELED) {
                    query.state = QueryState.RUNNING;
                } else if (isStale) {
                    query.state = QueryState.STALE;
                }

                if (!isStillRunning) {
                    query.finishedAt = new Date().toISOString();
                }
            }

            break;
        }

        case GET_QUERY_RESULTSET.SUCCEEDED: {
            const { id } = action.params;
            const { columns, records } = action.payload;
            const query = draft.byQueryId[id];

            if (!isNil(query) && query.state !== QueryState.CANCELED) {
                query.state = QueryState.SUCCESS;
                query.data = {
                    columns,
                    records
                };
            }

            if (action.meta?.queryKey) {
                const metadata = get(action.meta?.queryKey, draft);

                if (metadata && metadata.state !== QueryState.CANCELED) {
                    metadata.state = QueryState.SUCCESS;
                }
            } else {
                console.warn('query results with no meta', action);
            }

            break;
        }

        case GET_QUERY_RESULTSET.FAILED: {
            const { id } = action.params;
            const query = draft.byQueryId[id];

            if (!isNil(query)) {
                if (query.state !== QueryState.CANCELED) {
                    query.state = QueryState.FAILED;
                }
                query.error = extractError(action.payload);
            }

            const { queryKey } = action.meta;

            if (queryKey) {
                const metadata = get(queryKey, draft);

                if (metadata && metadata.state !== QueryState.CANCELED) {
                    metadata.state = QueryState.FAILED;
                }
            }

            break;
        }

        case CANCEL_ALL_QUERIES.STARTED:
            draft.isCancelingAllQueries = true;
            break;
        case CANCEL_ALL_QUERIES.ENDED:
            draft.isCancelingAllQueries = false;
            break;

        case CANCEL_QUERY.STARTED: {
            const { id } = action.payload;
            const query = draft.byQueryId[id];

            if (!isNil(query)) {
                query.state = QueryState.CANCELED;
            }

            const { queryKey } = action.meta;

            if (queryKey) {
                const metadata = get(queryKey, draft);

                if (metadata?.state) {
                    metadata.state = QueryState.CANCELED;
                }
            }

            break;
        }

        case DELETE_DATASET_QUERY: {
            const { entityId } = action.payload;

            if (draft.byEntityId[entityId]) {
                const { queryId } = draft.byEntityId[entityId];

                if (queryId) {
                    delete draft.byQueryId[queryId];
                }

                delete draft.byEntityId[entityId];
            }

            break;
        }

        case DELETE_JOIN_QUERIES: {
            if (draft.operations.join.preview.queryId) {
                delete draft.byQueryId[draft.operations.join.preview.queryId];
            }

            draft.operations.join.preview = {};

            for (const [key, value] of Object.entries(draft.operations.join.validations)) {
                if (value.queryId) {
                    delete draft.byQueryId[value.queryId];
                }

                draft.operations.join.validations[key] = {};
            }

            break;
        }

        case DELETE_EXTRACT_JSON_QUERIES: {
            if (draft.operations.extractJson.preview.queryId) {
                delete draft.byQueryId[draft.operations.extractJson.preview.queryId];
            }
            if (draft.operations.extractJson.metrics.queryId) {
                delete draft.byQueryId[draft.operations.extractJson.metrics.queryId];
            }
            draft.operations.extractJson.metrics = {};
            break;
        }

        case DELETE_FILTERED_METRICS_QUERIES: {
            if (draft.ROIReporting.tableMetrics.filtered.queryId) {
                delete draft.byQueryId[draft.ROIReporting.tableMetrics.filtered.queryId];
            }
            draft.ROIReporting.tableMetrics.filtered = {};
            break;
        }

        case DELETE_EXPLORE_QUERY: {
            const { queryUuid } = action.payload;

            if (draft.byExploreQueryUuid[queryUuid]) {
                const { queryId } = draft.byExploreQueryUuid[queryUuid];

                if (queryId) {
                    delete draft.byQueryId[queryId];
                }

                delete draft.byExploreQueryUuid[queryUuid];
            }
            break;
        }

        case DELETE_FQN_QUERY: {
            const { fqn } = action.payload;

            if (draft.byFQN[toStringFqn(fqn)]) {
                const { queryId } = draft.byFQN[toStringFqn(fqn)];

                if (queryId) {
                    delete draft.byQueryId[queryId];
                }

                delete draft.byFQN[toStringFqn(fqn)];
            }

            break;
        }

        case DELETE_ROW_COUNT_QUERY: {
            const { fqnOrId } = action.payload;
            const stringFqnOrId = toStringFqn(fqnOrId);

            if (draft.metrics.rowCount.byFqnOrId[stringFqnOrId]) {
                const { queryId } = draft.metrics.rowCount.byFqnOrId[stringFqnOrId];

                if (queryId) {
                    delete draft.byQueryId[queryId];
                }

                delete draft.metrics.rowCount.byFqnOrId[stringFqnOrId];
            }

            break;
        }

        case DELETE_QUERY: {
            const { queryKey } = action.payload;

            const metadata = get(queryKey, draft);

            if (metadata) {
                const queryId = metadata.queryId;

                deletePath(draft, parseKey(queryKey));
                delete draft.byQueryId[queryId];
            }

            break;
        }

        case DELETE_MULTIJOIN_VALIDATION_QUERIES: {
            Object.values(draft.operations.multiJoin.validations.unmatchedRows).forEach((value) => {
                if (value.queryId) {
                    delete draft.byQueryId[value.queryId];
                }
            });

            draft.operations.multiJoin.validations = {
                matchedRows: {},
                unmatchedRows: {},
                multiplication: {}
            };

            break;
        }

        case REHYDRATE: {
            if ('snowflakeQueries' in action.payload) {
                const { byQueryId, byExploreQueryUuid } = action.payload.snowflakeQueries;

                const filteredByQueryId = Object.entries(byQueryId).reduce((acc, [id, q]) => {
                    if (isNil(q.queryId) || isNil(q.queryKey)) {
                        return acc;
                    }

                    if (q.finishedAt) {
                        const finishedAt = DateTime.fromISO(q.finishedAt);

                        if (finishedAt.diffNow('days').days < -7) {
                            return acc;
                        }
                    }

                    acc[id] = q;

                    return acc;
                }, {});

                draft.byQueryId = filteredByQueryId;

                const filteredByExploreQueryUuid = Object.entries(byExploreQueryUuid).reduce(
                    (acc, [id, m]) => {
                        if (filteredByQueryId.hasOwnProperty(m.queryId)) {
                            acc[id] = m;
                        }

                        return acc;
                    },
                    {}
                );

                draft.byExploreQueryUuid = filteredByExploreQueryUuid;
            }
            break;
        }

        case CLEANUP_AND_INVALIDATE_QUERIES: {
            return set(action.payload.queryKey, {}, draft);
        }

        case RESTORE_QUERY: {
            const { queryId, query } = action.payload;
            const { queryKey } = query._context.meta;

            ensurePathExists(draft, parseKey(queryKey));

            const storeMetadata = get(queryKey, draft);

            Object.assign(storeMetadata, query._context.meta);

            draft.byQueryId[queryId] ||= {};
            draft.byQueryId[queryId].queryId = queryId;
            draft.byQueryId[queryId].queryKey = queryKey;
            draft.byQueryId[queryId].startedAt = query.submittedAt;
            draft.byQueryId[queryId].completedAt = query.finishedAt;
            draft.byQueryId[queryId].isQueued = query.status === 'QUEUED';

            break;
        }

        default:
    }
}, INITIAL_STATE);
